'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shadcn/lib/utils';

interface Props {
    children: ReactNode;
    /** Oneskorenie v ms - na stupnovite odhalenie viacerych prvkov za sebou. */
    delay?: number;
    /** Ako vela sa prvok posunie zdola. Default 24px. */
    distance?: number;
    className?: string;
}

/**
 * Odhalenie obsahu pri scrollovani.
 *
 * Rovnaka krivka ako zvysok systemu (--ease-rx z globals.css). Respektuje
 * prefers-reduced-motion - vtedy sa obsah zobrazi rovno, bez animacie.
 */
export function Reveal(props: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduced) {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: '0px 0px -60px 0px' },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={cn('will-change-[opacity,transform]', props.className)}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : `translateY(${props.distance ?? 24}px)`,
                transition: `opacity 700ms var(--ease-rx) ${props.delay ?? 0}ms, transform 700ms var(--ease-rx) ${props.delay ?? 0}ms`,
            }}
        >
            {props.children}
        </div>
    );
}
